import { inject, Injectable } from '@angular/core';
import { collection, getDocs, Firestore } from '@angular/fire/firestore';
import type { ChildSelectorProfile } from '../ui/molecules/child-selector/child-selector';

const CACHE_TTL_MS = 30_000; // 30s — children rarely change mid-session

/**
 * Shared service for loading children profiles from Firestore.
 * Caches results for 30s to avoid redundant reads across pages.
 */
@Injectable({ providedIn: 'root' })
export class ChildrenDataService {
  private readonly firestore = inject(Firestore);

  private cache: { uid: string; data: ChildSelectorProfile[]; ts: number } | null = null;
  private inflight: Promise<ChildSelectorProfile[]> | null = null;

  async loadChildren(uid: string): Promise<ChildSelectorProfile[]> {
    // Return cache if fresh
    if (this.cache && this.cache.uid === uid && Date.now() - this.cache.ts < CACHE_TTL_MS) {
      return this.cache.data;
    }

    // Deduplicate concurrent calls
    if (this.inflight) return this.inflight;

    this.inflight = this.fetchChildren(uid);
    try {
      const result = await this.inflight;
      this.cache = { uid, data: result, ts: Date.now() };
      return result;
    } finally {
      this.inflight = null;
    }
  }

  /** Invalidate cache (call after adding/editing a child). */
  invalidate(): void {
    this.cache = null;
  }

  private async fetchChildren(uid: string): Promise<ChildSelectorProfile[]> {
    const childrenRef = collection(this.firestore, `users/${uid}/children`);
    const snapshot = await getDocs(childrenRef);
    return snapshot.docs
      .filter((d) => {
        const status = d.data()['status'] as string | undefined;
        return !status || status === 'active';
      })
      .map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: (data['name'] as string) ?? '',
          birthDate: ((data['birthDate'] as string) ?? '').slice(0, 10),
          photoURL: (data['photoURL'] as string | undefined) ?? undefined,
          gender: (data['gender'] as 'boy' | 'girl' | undefined) ?? undefined,
        };
      });
  }
}
